import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { StrategyExecution, Trade, Order, RsiStraddleConfig, TrendingExpiryConfig } from '../types'
import { getLotSize } from '../utils/lotSize'

// ─── API Base URL ───────────────────────────────────────
import { API_BASE } from '../utils/api'

// ─── Auth Headers Helper ────────────────────────────────
function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('lamoney-token')
  if (token) {
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
  }
  return { 'Content-Type': 'application/json' }
}

function authFetchHeaders(): Record<string, string> {
  const token = localStorage.getItem('lamoney-token')
  if (token) {
    return { 'Authorization': `Bearer ${token}` }
  }
  return {}
}

interface ExecutionContextType {
  executions: StrategyExecution[]
  trades: Trade[]
  orders: Order[]
  startExecution: (id: string) => void
  stopExecution: (id: string) => void
  pauseExecution: (id: string) => void
  partialExit: (id: string, cePercent: number, pePercent: number) => void
  panicExit: (level: 'set' | 'strategy' | 'broker' | 'all', targetId?: string) => void
  createExecution: (exec: Omit<StrategyExecution, 'id' | 'createdAt' | 'updatedAt'>) => void
  deleteExecution: (id: string) => void
  refreshExecutions: () => Promise<void>
  refreshTrades: () => Promise<void>
  isLoading: boolean
}

const ExecutionContext = createContext<ExecutionContextType | null>(null)

// ─── Default strategy configs (used when creating new sets) ─

export const rsiDefaults: RsiStraddleConfig = {
  underlying: 'NIFTY', expiryType: 'weekly', positionType: 'straddle', quantityLots: 1,
  strikeSelectionMode: 'ATM', strikeOffset: 0, targetDelta: 0.50,
  entryTime: '09:30', noReentryAfter: '14:30', noReentryAfterStrict: '14:30', squareOffTime: '15:20',
  slPercent: 0.20, exitPercent: 0.40, minPremiumSl: 10, premiumFloor: 50,
  maxReentryCount: 2, marginPerLot: 150000,
  reentryRsiThresholdHigh: 60, reentryRsiCooldown: 55, reentryRsiPeriod: 14,
  reentryRsiTimeframe: '3min', candleTimeframe: '10min',
  orderType: 'MARKET', limitBuffer: 2, eventDaySkip: true,
}

export const trendDefaults: TrendingExpiryConfig = {
  underlying: 'NIFTY', expiryType: 'weekly', positionType: 'straddle', quantityLots: 1,
  strikeSelectionMode: 'ATM', strikeOffset: 0, targetDelta: 0.50, extraLegStrikeOffset: 1,
  entryStartTime: '09:30', entryEndTime: '14:30', entryIntervalMinutes: 30, squareOffTime: '15:20',
  callSLPercent: 0.80, putSLPercent: 0.70, profitTargetPercent: 0.90,
  extraLegSLPercent: 0.50, extraLegTargetPercent: 0.90, extraLegOncePerSide: true,
  extraLegSameQty: true, extraLegQuantityLots: 1,
  perStraddleLossPercent: 0.02, totalStrategyLossPercent: 0.03, marginPerLot: 150000,
  orderType: 'MARKET', limitBuffer: 2, eventDaySkip: true,
}

// ─── Helper: classify executions by time ─────────────────

export type ExecutionCategory = 'today' | 'active' | 'archived'

export function categorizeExecution(exec: StrategyExecution): ExecutionCategory {
  const today = new Date().toISOString().split('T')[0]
  const execDate = exec.createdAt.split('T')[0]

  // Active overnight strategies (future use)
  if (exec.status === 'running' && execDate !== today) {
    return 'active'
  }

  // Today's strategies (created today OR running)
  if (execDate === today || exec.status === 'running' || exec.status === 'paused') {
    return 'today'
  }

  // Archived: completed/stopped from previous days
  return 'archived'
}

export function categorizeExecutions(execs: StrategyExecution[]): {
  today: StrategyExecution[]
  active: StrategyExecution[]
  archived: StrategyExecution[]
} {
  const result = { today: [] as StrategyExecution[], active: [] as StrategyExecution[], archived: [] as StrategyExecution[] }
  for (const exec of execs) {
    result[categorizeExecution(exec)].push(exec)
  }
  // Archived: last 90 days only
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
  result.archived = result.archived.filter((e) => e.createdAt >= ninetyDaysAgo)
  return result
}

// ─── Provider ────────────────────────────────────────────

export function ExecutionProvider({ children }: { children: React.ReactNode }) {
  const [executions, setExecutions] = useState<StrategyExecution[]>([])
  const [trades, setTrades] = useState<Trade[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // ─── Derive orders from executions + trades ──────
  const orders: Order[] = useMemo(() => {
    const derived: Order[] = []

    // 1. Generate entry orders from running/stopped/squared_off executions
    for (const exec of executions) {
      if (exec.status === 'stopped' && !exec.updatedAt) continue // never started
      const p = exec.params as any
      const lots = p?.quantityLots || 1
      const lotSize = getLotSize(exec.underlying)
      const qty = lots * lotSize
      const straddle = exec.state.currentStraddle
      const ts = exec.updatedAt || exec.createdAt
      const isFilled = exec.status === 'running' || exec.status === 'squared_off' || exec.status === 'stopped'

      // CE leg order
      derived.push({
        id: `${exec.id}-entry-ce`,
        executionId: exec.id,
        orderId: `ORD-${exec.id.slice(-6)}-CE`,
        timestamp: ts,
        underlying: exec.underlying,
        strike: straddle?.strike || 0,
        optionType: 'CE',
        side: 'SELL',
        orderType: (p?.orderType as 'MARKET' | 'LIMIT') || 'MARKET',
        quantity: qty,
        price: (straddle as any)?.cePrice || straddle?.entryPrice || exec.state.initialPremium || 0,
        status: isFilled ? 'filled' : 'placed',
        filledQty: isFilled ? qty : undefined,
        filledPrice: isFilled ? ((straddle as any)?.cePrice || straddle?.entryPrice || exec.state.initialPremium || 0) : undefined,
        reason: 'Strategy entry',
        strategyType: exec.strategyType,
      })

      // PE leg order
      derived.push({
        id: `${exec.id}-entry-pe`,
        executionId: exec.id,
        orderId: `ORD-${exec.id.slice(-6)}-PE`,
        timestamp: ts,
        underlying: exec.underlying,
        strike: straddle?.strike || 0,
        optionType: 'PE',
        side: 'SELL',
        orderType: (p?.orderType as 'MARKET' | 'LIMIT') || 'MARKET',
        quantity: qty,
        price: (straddle as any)?.pePrice || straddle?.entryPrice || exec.state.initialPremium || 0,
        status: isFilled ? 'filled' : 'placed',
        filledQty: isFilled ? qty : undefined,
        filledPrice: isFilled ? ((straddle as any)?.pePrice || straddle?.entryPrice || exec.state.initialPremium || 0) : undefined,
        reason: 'Strategy entry',
        strategyType: exec.strategyType,
      })

      // Squared off / panic exit — add exit orders
      if (exec.status === 'squared_off') {
        derived.push({
          id: `${exec.id}-exit-ce`,
          executionId: exec.id,
          orderId: `ORD-${exec.id.slice(-6)}-XCE`,
          timestamp: exec.updatedAt || ts,
          underlying: exec.underlying,
          strike: straddle?.strike || 0,
          optionType: 'CE',
          side: 'BUY',
          orderType: 'MARKET',
          quantity: qty,
          price: exec.state.currentPremium || 0,
          status: 'filled',
          filledQty: qty,
          filledPrice: exec.state.currentPremium || 0,
          reason: 'Panic / Square off',
          strategyType: exec.strategyType,
        })
        derived.push({
          id: `${exec.id}-exit-pe`,
          executionId: exec.id,
          orderId: `ORD-${exec.id.slice(-6)}-XPE`,
          timestamp: exec.updatedAt || ts,
          underlying: exec.underlying,
          strike: straddle?.strike || 0,
          optionType: 'PE',
          side: 'BUY',
          orderType: 'MARKET',
          quantity: qty,
          price: exec.state.currentPremium || 0,
          status: 'filled',
          filledQty: qty,
          filledPrice: exec.state.currentPremium || 0,
          reason: 'Panic / Square off',
          strategyType: exec.strategyType,
        })
      }

      // Partial exit orders
      if (exec.state.lastPartialExit) {
        const pe = exec.state.lastPartialExit
        if (pe.cePercent > 0) {
          const exitQty = Math.round(qty * pe.cePercent / 100)
          derived.push({
            id: `${exec.id}-partial-ce`,
            executionId: exec.id,
            orderId: `ORD-${exec.id.slice(-6)}-PCE`,
            timestamp: pe.timestamp,
            underlying: exec.underlying,
            strike: straddle?.strike || 0,
            optionType: 'CE',
            side: 'BUY',
            orderType: 'MARKET',
            quantity: exitQty,
            price: exec.state.currentPremium || 0,
            status: 'filled',
            filledQty: exitQty,
            filledPrice: exec.state.currentPremium || 0,
            reason: `Partial exit ${pe.cePercent}%`,
            strategyType: exec.strategyType,
          })
        }
        if (pe.pePercent > 0) {
          const exitQty = Math.round(qty * pe.pePercent / 100)
          derived.push({
            id: `${exec.id}-partial-pe`,
            executionId: exec.id,
            orderId: `ORD-${exec.id.slice(-6)}-PPE`,
            timestamp: pe.timestamp,
            underlying: exec.underlying,
            strike: straddle?.strike || 0,
            optionType: 'PE',
            side: 'BUY',
            orderType: 'MARKET',
            quantity: exitQty,
            price: exec.state.currentPremium || 0,
            status: 'filled',
            filledQty: exitQty,
            filledPrice: exec.state.currentPremium || 0,
            reason: `Partial exit ${pe.pePercent}%`,
            strategyType: exec.strategyType,
          })
        }
      }
    }

    // Sort newest first
    derived.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
    return derived
  }, [executions])

  // ─── API Fetchers ───────────────────────────────────

  const refreshExecutions = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/executions`, { headers: authFetchHeaders() })
      if (res.status === 401) { console.warn('Auth expired'); return }
      if (res.ok) {
        const data = await res.json() as StrategyExecution[]
        setExecutions(data)
      }
    } catch (err) {
      console.warn('Failed to fetch executions:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshTrades = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/trades?limit=200`, { headers: authFetchHeaders() })
      if (res.status === 401) return
      if (res.ok) {
        const data = await res.json() as { trades: Trade[] }
        setTrades(data.trades || [])
      }
    } catch (err) {
      console.warn('Failed to fetch trades:', err)
    }
  }, [])

  // ─── Execution Actions ──────────────────────────────

  const startExecution = useCallback(async (id: string) => {
    // Optimistic update — change status immediately for responsive UI
    setExecutions((prev) =>
      prev.map((e) => e.id === id ? { ...e, status: 'running', updatedAt: new Date().toISOString() } : e)
    )
    try {
      await fetch(`${API_BASE}/api/executions/${id}/start`, { method: 'POST', headers: authFetchHeaders() })
    } catch (err) {
      console.warn('API not available for start, using local state:', err)
    }
  }, [])

  const stopExecution = useCallback(async (id: string) => {
    // Optimistic update
    setExecutions((prev) =>
      prev.map((e) => e.id === id ? { ...e, status: 'stopped', updatedAt: new Date().toISOString() } : e)
    )
    try {
      await fetch(`${API_BASE}/api/executions/${id}/stop`, { method: 'POST', headers: authFetchHeaders() })
    } catch (err) {
      console.warn('API not available for stop, using local state:', err)
    }
  }, [])

  const pauseExecution = useCallback(async (id: string) => {
    // Optimistic update
    setExecutions((prev) =>
      prev.map((e) => e.id === id ? { ...e, status: 'paused' as const, updatedAt: new Date().toISOString() } : e)
    )
    try {
      await fetch(`${API_BASE}/api/executions/${id}/pause`, { method: 'POST', headers: authFetchHeaders() })
    } catch (err) {
      console.warn('Pause API unavailable:', err)
    }
  }, [])

  const partialExit = useCallback(async (id: string, cePercent: number, pePercent: number) => {
    // Optimistic UI update — PnL only, no flag yet
    setExecutions((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e
        const exitPnl = Math.round(e.state.pnl * ((cePercent + pePercent) / 200))
        return {
          ...e,
          state: {
            ...e.state,
            pnl: e.state.pnl - exitPnl,
            mtm: {
              ...e.state.mtm,
              realizedPnl: e.state.mtm.realizedPnl + exitPnl,
              unrealizedPnl: e.state.mtm.unrealizedPnl - exitPnl,
              lastUpdated: new Date().toISOString(),
            },
          },
          updatedAt: new Date().toISOString(),
        }
      })
    )
    // Call backend API — flag only after confirmed execution
    try {
      const res = await fetch(`${API_BASE}/api/executions/${id}/partial-exit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ce_percent: cePercent, pe_percent: pePercent }),
      })
      if (res.ok) {
        setExecutions((prev) =>
          prev.map((e) => e.id !== id ? e : {
            ...e,
            state: {
              ...e.state,
              lastPartialExit: { cePercent, pePercent, timestamp: new Date().toISOString() },
            },
          })
        )
      }
    } catch (err) {
      console.warn('API not available for partial exit, using local state:', err)
    }
  }, [])

  const panicExit = useCallback(async (level: 'set' | 'strategy' | 'broker' | 'all', targetId?: string) => {
    const toStop = executions.filter((e) => {
      if (e.status === 'stopped' || e.status === 'squared_off') return false
      if (level === 'all') return true
      if (level === 'set' && e.id === targetId) return true
      if (level === 'strategy' && e.strategyType === targetId) return true
      if (level === 'broker' && e.brokerId === targetId) return true
      return false
    })

    for (const exec of toStop) {
      try {
        await fetch(`${API_BASE}/api/executions/${exec.id}/panic`, { method: 'POST', headers: authFetchHeaders() })
      } catch (err) {
        console.error(`Panic exit failed for ${exec.id}:`, err)
      }
    }

    setExecutions((prev) =>
      prev.map((e) => {
        const shouldStop = toStop.some((s) => s.id === e.id)
        if (!shouldStop) return e
        return {
          ...e,
          status: 'squared_off',
          state: {
            ...e.state,
            currentPremium: 0,
            slPrice: 0,
            targetPrice: 0,
            currentStraddle: null,
            mtm: {
              ...e.state.mtm,
              realizedPnl: e.state.mtm.realizedPnl + e.state.pnl,
              unrealizedPnl: 0,
              setPnl: e.state.pnl,
              lastUpdated: new Date().toISOString(),
            },
          },
          updatedAt: new Date().toISOString(),
        }
      })
    )
  }, [executions])

  const createExecution = useCallback(async (exec: Omit<StrategyExecution, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const res = await fetch(`${API_BASE}/api/executions`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          strategy_type: exec.strategyType,
          set_name: exec.setName,
          trader_id: exec.traderId,
          broker_id: exec.brokerId,
          underlying: exec.underlying,
          mode: exec.mode,
          params: exec.params,
          margin: exec.margin,
        }),
      })

      if (res.ok) {
        const data = await res.json() as { id: string }
        const newExec: StrategyExecution = {
          ...exec,
          id: data.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        setExecutions((prev) => [...prev, newExec])
      }
    } catch (err) {
      console.error('Failed to create execution:', err)
      // Fallback: create locally
      const newExec: StrategyExecution = {
        ...exec,
        id: `e-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      setExecutions((prev) => [...prev, newExec])
    }
  }, [])

  const deleteExecution = useCallback(async (id: string) => {
    // Optimistic removal
    setExecutions((prev) => prev.filter((e) => e.id !== id))
    try {
      await fetch(`${API_BASE}/api/executions/${id}`, { method: 'DELETE', headers: authFetchHeaders() })
    } catch (err) {
      console.warn('Delete API unavailable:', err)
    }
  }, [])

  return (
    <ExecutionContext.Provider value={{
      executions, trades, orders, isLoading,
      startExecution, stopExecution, pauseExecution, partialExit, panicExit,
      createExecution, deleteExecution, refreshExecutions, refreshTrades,
    }}>
      {children}
    </ExecutionContext.Provider>
  )
}

export function useExecutions() {
  const ctx = useContext(ExecutionContext)
  if (!ctx) throw new Error('useExecutions must be inside ExecutionProvider')
  return ctx
}
